import React from 'react';
import { usePokerEvents } from '@/components/utils/hooks/events/usePokerEvents';
import { FlutterDataPayload, GLOBAL_FLUTTER_CHANNELS } from '@/components/utils/hooks/events/constants';

declare global {
  interface Window {
    flutter_inappwebview?: {
      // new flutter channel
      callHandler?: (channel: string, data: any) => void;
    };
    flutter?: {
      // legacy flutter channel
      callHandler?: (channel: string, data: any) => void;
    };
    // qa channel
    qa?: {
      // TODO: replace this with the correct type
      callHandler?: (channel: string, data: any) => void;
    };
  }
}

/**
 * useFlutterEvents
 * @param data
 */
export function useFlutterEvents(data?: { debugPayload?: FlutterDataPayload }) {
  const [infoReceived, setInfoReceived] = React.useState<any>(null);

  // usePokerEvents hook checks if the poker event is active and returns the callHandler function which is used to map & send data to the poker app
  const pokerEvents = usePokerEvents();

  const send = React.useMemo(() => {
    let funct = (channel: string, data: FlutterDataPayload) => {};
    if (window.flutter_inappwebview?.callHandler) {
      funct = window.flutter_inappwebview.callHandler;
    } else if (window.flutter?.callHandler) {
      funct = window.flutter.callHandler;
    } else if (window.qa?.callHandler) {
      funct = window.qa.callHandler;
    } else if (
      pokerEvents.isPokerEventActive &&
      pokerEvents.callHandler &&
      typeof pokerEvents.callHandler === 'function'
    ) {
      funct = pokerEvents.callHandler;
    }
    return funct;
  }, [pokerEvents.callHandler, pokerEvents.isPokerEventActive]);

  const sendDataToFlutter = React.useCallback(
    (channel: string, data: FlutterDataPayload) => {
      if (channel !== GLOBAL_FLUTTER_CHANNELS.DEBUG && channel !== GLOBAL_FLUTTER_CHANNELS.LOG) {
        send(GLOBAL_FLUTTER_CHANNELS.LOG, {
          event: 'log',
          channel,
          data,
        });
      }
      send(channel, data);
    },
    [send],
  );

  const eventListenerFunction = React.useCallback((e: any) => {
    setInfoReceived(e);
  }, []);

  const removeEventListeners = React.useCallback(() => {
    window.removeEventListener('message', eventListenerFunction);
  }, [eventListenerFunction]);

  const loadEventListeners = React.useCallback(() => {
    // todo: add here validations. we should not load event listeners if we do not have credentials?!

    const debug: FlutterDataPayload = data?.debugPayload ? { ...data.debugPayload } : { event: 'debug' };
    debug.channels = {
      ...GLOBAL_FLUTTER_CHANNELS,
      ...debug.channels,
    };
    sendDataToFlutter(GLOBAL_FLUTTER_CHANNELS.DEBUG, { message: 'event-listeners-loaded', ...debug });
    // remove event listener to prevent double loading
    removeEventListeners();
    // prevent double loading
    window.addEventListener('message', eventListenerFunction, false);
  }, [data?.debugPayload, sendDataToFlutter, removeEventListeners, eventListenerFunction]);

  React.useEffect(() => {
    sendDataToFlutter(GLOBAL_FLUTTER_CHANNELS.DEBUG, data?.debugPayload ?? { event: 'debug' });
  }, [data?.debugPayload, sendDataToFlutter]);

  React.useEffect(() => {
    removeEventListeners();
    loadEventListeners();
    return () => {
      removeEventListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return React.useMemo(
    () => ({
      send: sendDataToFlutter,
      infoReceived,
    }),
    [infoReceived, sendDataToFlutter],
  );
}